import {compact} from 'lodash';
import React, {FC, ReactNode, useCallback, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {AsyncHandlerOf, Handler, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownItemRightIcon} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemRightIcon';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {CardGroupModel} from '../../../../models/api/cardGroupModel';
import {IconBox} from '../../iconBox/iconBox';
import {DropdownCategory} from '../dropdownCategory';
import {DropdownEmpty} from '../dropdownEmpty';
import {DropdownItemAccentIcon} from '../dropdownItemAccentIcon';
import {DropdownLoader} from '../dropdownLoader';
import {DeprecatedSearchDropdown} from '../search/deprecatedSearchDropdown';
import {TypedRowValue} from '../source/dropdownSource';
import {SyncDropdownSource} from '../source/syncDropdownSource';

/*
 * Constants.
 */

const maxDropdownContentHeight = 258; // px.
const dropdownRowHeight = 30; // px.
const dropdownPrivateBannerRowHeight = 56; // px.

enum ModifyCardGroupsDropdownRowValuesEnum {
  CATEGORY = 'CATEGORY',
  NEW_ACTION = 'NEW_ACTION',
  GROUP = 'GROUP',
  PRIVATE_CONTACTS_BANNER = 'PRIVATE_CONTACTS_BANNER',
}

export enum ModifyCardGroupsDropdownTypesEnum {
  ADD = 'add',
  REMOVE = 'remove',
}

/*
 * Props.
 */

type ModifyCardGroupsOnSortGroups = (groups: ReadonlyArray<CardGroupModel>) => ReadonlyArray<CardGroupModel>;

interface ModifyCardGroupsDropdownProps {
  popoverProps: PopoverTransferProps;
  /** Controls what is displayed for the dropdown. */
  type: ModifyCardGroupsDropdownTypesEnum;
  /** Namespace for the groups. */
  namespace: string;
  /** Groups that should be displayed. If specified, only groups ids passed in will be displayed. */
  availableGroupIds?: ReadonlyArray<number>;
  /** Groups that should have disabled checkboxes. */
  disabledGroupIds?: ReadonlyArray<number>;
  /** Groups that should be initially selected. */
  alreadySelectedGroupIds?: ReadonlyArray<number>;

  /** If passed in, a footer will be rendered. */
  renderFooter?: RendererOf<ReadonlyArray<CardGroupModel>>;

  /** Fetches all groups for the passed in namespace. */
  fetchCardGroupsAsync: AsyncHandlerOf<string, ReadonlyArray<CardGroupModel>>;
  /** When contacts are fetched, if defined this will sort the results. */
  onSortGroups?: ModifyCardGroupsOnSortGroups;
  /** If defined, a "New group" option will be displayed in the dropdown. */
  onNewGroupClick?: Handler;
}

type ModifyCardGroupsDropdownRowTypedValue =
  | TypedRowValue<ModifyCardGroupsDropdownRowValuesEnum.CATEGORY, ReactNode>
  | TypedRowValue<ModifyCardGroupsDropdownRowValuesEnum.PRIVATE_CONTACTS_BANNER, ReactNode>
  | TypedRowValue<ModifyCardGroupsDropdownRowValuesEnum.NEW_ACTION, ReactNode>
  | TypedRowValue<ModifyCardGroupsDropdownRowValuesEnum.GROUP, CardGroupModel>;

/*
 * Style.
 */

const StyledDropdownItemContent = styled(DropdownItemContent)`
  display: flex;
  flex-flow: row;
  overflow: hidden;
`;

interface StyledTooltipOverflowConditionProps {
  $isSelected: boolean;
}

const StyledTooltipOverflowCondition = styled(TooltipOverflowCondition)<StyledTooltipOverflowConditionProps>`
  direction: ltr;

  ${(p) =>
    p.$isSelected &&
    css`
      font-weight: ${fontWeights.bold};
    `}
`;

const StyledIcon = styled(Icon)`
  margin: 4px 0 0 4px;
`;

const StyledDropdownItemAccentIcon = styled(DropdownItemAccentIcon)`
  margin-top: 2px;
`;

const StyledIconBox = styled(IconBox)`
  margin: -2px 4px 0px 4px;
  font-size: ${fontSizes.verySmall};
`;

/*
 * Component.
 */

export const ModifyCardGroupsDropdown: FC<ModifyCardGroupsDropdownProps> = (props) => {
  const theme = useUserTheme();
  const {
    popoverProps,
    type,
    namespace,
    availableGroupIds,
    disabledGroupIds,
    alreadySelectedGroupIds,
    renderFooter,
    fetchCardGroupsAsync,
    onSortGroups,
    onNewGroupClick,
  } = props;
  const [groups, setGroups] = useState<ReadonlyArray<CardGroupModel>>([]);
  const [selectedGroups, setSelectedGroups] = useState<ReadonlyArray<CardGroupModel>>([]);
  const [status, setStatus] = useState(AsyncStatusesEnum.LOADING);
  const [isSearching, setIsSearching] = useState(false);

  const onNewGroupClickHandler = onNewGroupClick
    ? () => {
        setSelectedGroups([]);
        onNewGroupClick();
      }
    : undefined;

  const onGroupSelect = (group: CardGroupModel) => {
    setSelectedGroups((prevGroups) => [...prevGroups, group]);
  };

  const onGroupUnselect = (group: CardGroupModel) => {
    setSelectedGroups((prevGroups) => prevGroups.filter((g) => g !== group));
  };

  // Handles setting the status and fetching groups.
  const fetchGroups = useCallback(async () => {
    setStatus(AsyncStatusesEnum.LOADING);

    try {
      const fetchedGroups = await fetchCardGroupsAsync(namespace);
      setGroups(filterAndSortFetchedGroups(fetchedGroups, availableGroupIds, onSortGroups));
      setStatus(AsyncStatusesEnum.LOADED);
      const alreadySelectedGroups = alreadySelectedGroupIds
        ? fetchedGroups.filter((group) => alreadySelectedGroupIds.includes(group.id))
        : [];
      setSelectedGroups((prevGroups) => [...prevGroups, ...alreadySelectedGroups]);
    } catch (err) {
      setStatus(AsyncStatusesEnum.FAILED);
    }
  }, [fetchCardGroupsAsync, namespace, alreadySelectedGroupIds, availableGroupIds, onSortGroups]);

  // When we first load in, fetch all the groups.
  useInitialEffect(() => {
    fetchGroups();
  });

  const renderRow = (item: ModifyCardGroupsDropdownRowTypedValue) => {
    if (item.type !== ModifyCardGroupsDropdownRowValuesEnum.GROUP) {
      return item.value;
    }

    const group = item.value;
    const isSelected = Boolean(selectedGroups && selectedGroups.includes(group));
    const isDisabled = Boolean(disabledGroupIds && disabledGroupIds.includes(group.id));
    return (
      <TooltipCoordinator render={() => group.name} placement="top">
        <DropdownItem
          onSelect={(event) => {
            // Do not close the dropdown.
            event.preventDefault();
            if (isDisabled) {
              return;
            }

            if (!isSelected) {
              onGroupSelect(group);
            } else {
              onGroupUnselect(group);
            }
          }}
        >
          <StyledDropdownItemContent>
            <StyledTooltipOverflowCondition $isSelected={isSelected}>
              {group.name}
            </StyledTooltipOverflowCondition>
            {isTeammateNamespace(group.namespace) && (
              <StyledIcon name="lockFilledNewTiny" color={theme.greys.shade70} />
            )}
          </StyledDropdownItemContent>
          <DropdownItemCheckbox isChecked={isSelected} isDisabled={isDisabled} />
        </DropdownItem>
      </TooltipCoordinator>
    );
  };

  const onSearchQueryChange = (query: string) => {
    setIsSearching(Boolean(query));
  };

  const dropdownSource = new SyncDropdownSource<ModifyCardGroupsDropdownRowTypedValue>({
    items: buildDropdownItems(status, groups, isSearching, namespace, type, onNewGroupClickHandler),
    rowFilter,
    renderRow,
    rowHeightPredicate: (item) => {
      if (item?.type === ModifyCardGroupsDropdownRowValuesEnum.PRIVATE_CONTACTS_BANNER) {
        return dropdownPrivateBannerRowHeight;
      }
      return dropdownRowHeight;
    },
    renderEmpty: (_query: string) => renderEmptyOrLoading(status, fetchGroups),
  });

  return (
    <DeprecatedSearchDropdown
      {...popoverProps}
      title={renderDropdownTitle(type)}
      maxContentHeight={maxDropdownContentHeight}
      onSearchQueryChange={onSearchQueryChange}
      source={dropdownSource}
      shouldIncludeSearchIcon
      footer={maybeRenderFooter(status, selectedGroups, renderFooter)}
    />
  );
};

/*
 * Helpers.
 */

function maybeRenderFooter(
  status: AsyncStatusesEnum,
  selectedGroups: ReadonlyArray<CardGroupModel>,
  renderFooter?: RendererOf<ReadonlyArray<CardGroupModel>>,
) {
  if (!renderFooter || status !== AsyncStatusesEnum.LOADED) {
    return undefined;
  }
  return renderFooter(selectedGroups);
}

function renderDropdownTitle(type: ModifyCardGroupsDropdownTypesEnum) {
  if (type === ModifyCardGroupsDropdownTypesEnum.ADD) {
    return <Trans id="card-groups-dropdown-title-add-to">Add to</Trans>;
  }
  return <Trans id="card-groups-dropdown-title-remove-from">Remove from</Trans>;
}

function renderEmptyOrLoading(status: AsyncStatusesEnum, onRetryClick: Handler) {
  if (status === AsyncStatusesEnum.LOADED) {
    return <DropdownEmpty />;
  }

  return <DropdownLoader size={VisualSizesEnum.MEDIUM} status={status} onRetryClick={onRetryClick} />;
}

function rowFilter(items: ReadonlyArray<ModifyCardGroupsDropdownRowTypedValue>, query: string) {
  if (!query) {
    return items;
  }

  const queryInLowerCase = query.toLowerCase();
  const filteredGroups = items.filter(
    (item) =>
      item.type === ModifyCardGroupsDropdownRowValuesEnum.GROUP &&
      item.value.name.toLowerCase().includes(queryInLowerCase),
  );

  if (filteredGroups.length === 0) {
    return [];
  }
  return [
    ...items.filter((item) => item.type === ModifyCardGroupsDropdownRowValuesEnum.CATEGORY),
    ...filteredGroups,
  ];
}

function filterAndSortFetchedGroups(
  groups: ReadonlyArray<CardGroupModel>,
  availableGroupIds?: ReadonlyArray<number>,
  onSortGroups?: ModifyCardGroupsOnSortGroups,
) {
  const filteredGroups = availableGroupIds
    ? groups.filter((group) => availableGroupIds.includes(group.id))
    : groups;

  if (onSortGroups) {
    return onSortGroups(filteredGroups);
  }
  return filteredGroups;
}

function buildDropdownItems(
  status: AsyncStatusesEnum,
  groups: ReadonlyArray<CardGroupModel>,
  isSearching: boolean,
  namespace: string,
  type: ModifyCardGroupsDropdownTypesEnum,
  onNewGroupClickHandler?: Handler,
): ReadonlyArray<ModifyCardGroupsDropdownRowTypedValue> {
  // If the items are loading or errors, return nothing and let the empty handler display.
  if (status !== AsyncStatusesEnum.LOADED) {
    return [];
  }

  const shouldDisplayCreateHeaderAndAction = !isSearching;
  const shouldDisplayAllGroupsHeader = groups.length > 0 && type !== ModifyCardGroupsDropdownTypesEnum.REMOVE;
  const createHeaderAndAction = onNewGroupClickHandler
    ? [
        buildDropdownCategory(<Trans id="card-groups-dropdown-create-header">Create</Trans>),
        buildDropdownNewGroupAction(onNewGroupClickHandler),
      ]
    : [];
  const allGroupsHeader = buildDropdownCategory(renderAllGroupsText());

  return compact([
    maybeAddPrivateContactsBanner(namespace, type),
    ...(shouldDisplayCreateHeaderAndAction ? createHeaderAndAction : []),
    shouldDisplayAllGroupsHeader ? allGroupsHeader : undefined,
    ...groups.map(buildDropdownGroup),
  ]);
}

function buildDropdownCategory(value: React.ReactNode): ModifyCardGroupsDropdownRowTypedValue {
  return {
    type: ModifyCardGroupsDropdownRowValuesEnum.CATEGORY,
    value: <DropdownCategory>{value}</DropdownCategory>,
  };
}

function buildDropdownNewGroupAction(onNewGroupClickHandler: Handler): ModifyCardGroupsDropdownRowTypedValue {
  return {
    type: ModifyCardGroupsDropdownRowValuesEnum.NEW_ACTION,
    value: (
      <UserThemeConsumer>
        {(theme) => (
          <DropdownItem
            onSelect={(event) => {
              event.preventDefault();
              onNewGroupClickHandler();
            }}
            shouldReserveRightIcon
            renderRightContent={() => (
              <DropdownItemRightIcon
                label={null}
                rightIconName="chevronRightSmall"
                rightIconColor={theme.greys.shade60}
              />
            )}
          >
            <StyledDropdownItemAccentIcon
              label={null}
              iconName="plusCircleFilledSmall"
              shouldHideBackground
            />
            <DropdownItemContent>
              <Trans id="card-groups-dropdown-new-list-action">New list</Trans>
            </DropdownItemContent>
          </DropdownItem>
        )}
      </UserThemeConsumer>
    ),
  };
}

function buildDropdownGroup(value: CardGroupModel): ModifyCardGroupsDropdownRowTypedValue {
  return {
    type: ModifyCardGroupsDropdownRowValuesEnum.GROUP,
    value,
  };
}

function maybeAddPrivateContactsBanner(
  namespace: string,
  type: ModifyCardGroupsDropdownTypesEnum,
): ModifyCardGroupsDropdownRowTypedValue | undefined {
  if (!isTeammateNamespace(namespace) || type === ModifyCardGroupsDropdownTypesEnum.REMOVE) {
    return undefined;
  }
  return {
    type: ModifyCardGroupsDropdownRowValuesEnum.PRIVATE_CONTACTS_BANNER,
    value: (
      <UserThemeConsumer>
        {(theme) => (
          <StyledIconBox iconName="infoFilledSmall" iconColor={theme.palette.blue.shade40}>
            <Trans id="card-groups-dropdown-private-lists-contacts-banner-text">
              Private contacts can only be added to private lists.
            </Trans>
          </StyledIconBox>
        )}
      </UserThemeConsumer>
    ),
  };
}

function renderAllGroupsText() {
  return <Trans id="card-groups-dropdown-all-lists-header">All lists</Trans>;
}
